import { MicrophoneUnavailableError } from './MicrophoneUnavailableError';
import { StreamingResampler } from './StreamingResampler';
import { floatToPcm16, PCM_WORKLET_SOURCE } from './utils';

const FRAME_SECONDS = 0.1;

/** Captures microphone audio and emits PCM16 mono frames of ~100 ms resampled to the requested rate. */
export class PcmCapture {
    public static async requestMicrophone(): Promise<MediaStream> {
        try {
            return await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (error) {
            throw new MicrophoneUnavailableError(error);
        }
    }

    private context: AudioContext | null = null;
    private source: MediaStreamAudioSourceNode | null = null;
    private workletNode: AudioWorkletNode | null = null;
    private sink: GainNode | null = null;
    private resampler: StreamingResampler | null = null;

    private pendingFrames: Int16Array[] = [];
    private pendingSamples = 0;
    private frameSamples = 0;

    constructor(
        private readonly stream: MediaStream,
        private readonly onFrame: (frame: ArrayBuffer) => void,
    ) {}

    public async start(targetSampleRate: number): Promise<void> {
        this.context = new AudioContext();
        if (this.context.state === 'suspended') {
            await this.context.resume();
        }
        this.resampler = new StreamingResampler(this.context.sampleRate, targetSampleRate);
        this.frameSamples = Math.round(targetSampleRate * FRAME_SECONDS);

        const workletUrl = URL.createObjectURL(new Blob([PCM_WORKLET_SOURCE], { type: 'application/javascript' }));
        try {
            await this.context.audioWorklet.addModule(workletUrl);
        } finally {
            URL.revokeObjectURL(workletUrl);
        }

        this.source = this.context.createMediaStreamSource(this.stream);
        this.workletNode = new AudioWorkletNode(this.context, 'pcm-capture');
        this.workletNode.port.onmessage = event => this.pushBlock(event.data as Float32Array);
        this.sink = this.context.createGain();
        this.sink.gain.value = 0;
        this.source.connect(this.workletNode);
        this.workletNode.connect(this.sink);
        this.sink.connect(this.context.destination);
    }

    public async stop(): Promise<void> {
        try {
            this.source?.disconnect();
            this.workletNode?.disconnect();
            this.sink?.disconnect();
            if (this.context && this.context.state !== 'closed') {
                await this.context.close();
            }
        } catch {
            // capture teardown must never throw: the session is ending anyway
        } finally {
            this.source = null;
            this.workletNode = null;
            this.sink = null;
            this.context = null;
            this.stream.getTracks().forEach(track => track.stop());
        }
    }

    private pushBlock(block: Float32Array): void {
        if (!this.resampler) {
            return;
        }

        const pcm = floatToPcm16(this.resampler.process(block));
        if (pcm.length === 0) {
            return;
        }

        this.pendingFrames.push(pcm);
        this.pendingSamples += pcm.length;

        if (this.pendingSamples >= this.frameSamples) {
            this.flushFrame();
        }
    }

    private flushFrame(): void {
        const frame = new Int16Array(this.pendingSamples);
        let offset = 0;
        for (const piece of this.pendingFrames) {
            frame.set(piece, offset);
            offset += piece.length;
        }
        this.pendingFrames = [];
        this.pendingSamples = 0;

        this.onFrame(frame.buffer);
    }
}
